import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Loader2, Search, CheckCircle2, Ban } from 'lucide-react';
import ModalShell, { MODAL_CANCEL_CLASS, MODAL_SUBMIT_CLASS } from '../components/common/ModalShell';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';
import InvoiceModal from '../components/subscription/InvoiceModal';
import { activateSubscription, declineSubscription } from '../redux/subscriptionSlice';
import { subscriptionPrice } from '../utils/currency';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
// Each row in its own currency (a paid row names one), else the platform's —
// utils/currency.js, P24.
const money = (a, row) => (a != null && String(a) !== '' ? subscriptionPrice(a, row) : '—');
const cap = (s) => String(s || '').replace(/^\w/, (c) => c.toUpperCase());

// How the money actually arrives on a platform with no gateway (owner, 22 Sep
// 2026 — Q8). The admin records which one it was, with its reference.
const METHODS = [
  { id: 'upi', label: 'UPI' },
  { id: 'bank_transfer', label: 'Bank transfer' },
  { id: 'cash', label: 'Cash' },
  { id: 'other', label: 'Other' },
];

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'paid', label: 'Paid' },
  { id: 'pending', label: 'Pending' },
];

// Admin → Subscription → Payments. Every subscription payment across doctors:
// paid rows (with a downloadable invoice, same as the doctor sees) + pending plan
// changes doctors still owe. Tabs filter by status.
//
// Since 22 Sep 2026 the pending tab is where a plan actually starts: there is no
// payment gateway, so a doctor requests a plan, pays by UPI or bank transfer,
// and an admin records that here — "Mark paid & activate" with the reference is
// what switches the plan on. "Decline" clears the request instead.
export default function SubscriptionPayments() {
  const axiosInstance = useAxiosAuth();
  const { t } = useTranslation();
  // A missing name falls back to a neutral word, never to the doctorId
  // (owner, 29 Sep 2026: no system id on screen).
  const doctorNameOf = (row) => row?.doctorName || t('common.doctor_label', 'Doctor');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`${baseURL}/subscription-package/payments`);
        if (alive) setPayments(res.data?.payments || []);
      } catch {
        if (alive) { setPayments([]); toast.warn('Failed to load payments'); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusOf = (p) => p.status || 'paid';

  const dispatch = useDispatch();
  const [settle, setSettle] = useState(null);     // the pending row being marked paid
  const [form, setForm] = useState({ reference: '', method: 'upi', note: '' });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const res = await axiosInstance.get(`${baseURL}/subscription-package/payments`);
      setPayments(res.data?.payments || []);
    } catch {
      toast.warn('Failed to reload payments');
    }
  };

  const openSettle = (row) => {
    setForm({ reference: row.reference || '', method: 'upi', note: '' });
    setSettle(row);
  };

  const activate = async () => {
    if (!form.reference.trim()) { toast.warn('Enter the payment reference'); return; }
    setBusy(true);
    try {
      await dispatch(activateSubscription({
        doctorId: settle.doctorId, reference: form.reference.trim(), method: form.method, note: form.note.trim() || undefined,
      })).unwrap();
      toast.success(`${settle.packageName || 'Plan'} activated for ${doctorNameOf(settle)}`);
      setSettle(null);
      await reload();
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : 'Could not activate the plan');
    } finally {
      setBusy(false);
    }
  };

  const decline = async (row) => {
    const ask = await Swal.fire({
      title: 'Decline this request?',
      text: `${doctorNameOf(row)} asked for ${row.packageName || 'a plan'}. Their current plan is not changed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#4ABDBD',
      confirmButtonText: 'Decline',
    });
    if (!ask.isConfirmed) return;
    try {
      await dispatch(declineSubscription({ doctorId: row.doctorId })).unwrap();
      toast.success('Request declined');
      await reload();
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : 'Could not decline the request');
    }
  };

  const counts = useMemo(() => ({
    all: payments.length,
    paid: payments.filter((p) => statusOf(p) === 'paid').length,
    pending: payments.filter((p) => statusOf(p) === 'pending').length,
  }), [payments]);

  const rows = useMemo(() => {
    let list = tab === 'all' ? payments : payments.filter((p) => statusOf(p) === tab);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        String(p.doctorName || '').toLowerCase().includes(q) ||
        String(p.doctorId || '').toLowerCase().includes(q) ||
        String(p.packageName || '').toLowerCase().includes(q));
    }
    return list;
  }, [payments, tab, query]);

  return (
    <div className="min-h-full w-full" data-testid="subscription-payments-page">
      <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">Payments</h1>
      <p className="text-sm text-slate-500 mt-1 mb-4">Subscription payments across all doctors — paid invoices and pending plan changes.</p>

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TABS.map((tb) => {
          const on = tab === tb.id;
          return (
            <button
              key={tb.id}
              type="button"
              onClick={() => setTab(tb.id)}
              data-testid={`payments-tab-${tb.id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${on ? 'bg-[#4ABDBD] text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
            >
              {tb.label} <span className="opacity-70">({counts[tb.id]})</span>
            </button>
          );
        })}
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search doctor / plan"
            data-testid="payments-search"
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] w-56"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-md p-4 sm:p-5">
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="payments-loading">
            <Loader2 className="animate-spin text-[#4ABDBD]" size={30} />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 py-10 text-center" data-testid="payments-empty">No payments in this view.</p>
        ) : (
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-semibold">Doctor</th>
                  <th className="py-2 pr-3 font-semibold">Plan</th>
                  <th className="py-2 pr-3 font-semibold text-right">Amount</th>
                  <th className="py-2 pr-3 font-semibold">Date</th>
                  <th className="py-2 pr-3 font-semibold">Status</th>
                  <th className="py-2 pr-3 font-semibold text-right">Invoice</th>
                  <th className="py-2 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const isPaid = statusOf(p) === 'paid';
                  return (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`payment-row-${p.id}`}>
                      <td className="py-2.5 pr-3">
                        <div className="text-gray-800 font-medium whitespace-normal">{doctorNameOf(p)}</div>
                      </td>
                      <td className="py-2.5 pr-3 text-gray-700 whitespace-normal">
                        {p.packageName || '—'}
                        <span className="block text-[11px] text-slate-400">
                          {p.period ? cap(String(p.period)) : ''}
                          {p.reference ? `${p.period ? ' · ' : ''}${p.reference}` : ''}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-gray-800">
                        {money(p.amount, p)}
                        {Number(p.creditApplied) > 0 && (
                          <span className="block text-[10px] font-normal text-emerald-600">−{subscriptionPrice(p.creditApplied, p)} credit</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-600">{fmtDate(p.createdAt)}</td>
                      <td className="py-2.5 pr-3">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {cap(statusOf(p))}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        {p.invoiceAvailable ? (
                          <button
                            type="button"
                            onClick={() => setInvoice(p)}
                            data-testid={`view-invoice-${p.id}`}
                            className="inline-flex items-center gap-1 text-[#4ABDBD] hover:text-[#2D8A8A] font-semibold"
                          >
                            <FileText className="w-3.5 h-3.5" /> View
                          </button>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-2.5 text-right">
                        {isPaid ? (
                          <span className="text-slate-300 text-xs">—</span>
                        ) : (
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openSettle(p)}
                              data-testid={`activate-${p.id}`}
                              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:shadow-md transition-all"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> Mark paid &amp; activate
                            </button>
                            <button
                              type="button"
                              onClick={() => decline(p)}
                              data-testid={`decline-${p.id}`}
                              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 font-semibold hover:border-red-300 hover:text-red-500 transition-all"
                            >
                              <Ban className="w-3.5 h-3.5" /> Decline
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mark paid & activate — the reference is the whole point: it is the only
          trace of a payment that happened outside the platform. */}
      {settle && (
        <ModalShell
          title="Mark paid & activate"
          onClose={() => setSettle(null)}
          size="md"
          zClass="z-[100]"
          testId="activate-modal"
          footer={(
            <>
              <button type="button" onClick={() => setSettle(null)} className={MODAL_CANCEL_CLASS}>Cancel</button>
              <button
                type="button"
                onClick={activate}
                disabled={busy || !form.reference.trim()}
                data-testid="activate-confirm"
                className={MODAL_SUBMIT_CLASS}
              >
                {busy ? <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Activate plan
              </button>
            </>
          )}
        >
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                <strong>{doctorNameOf(settle)}</strong> — {settle.packageName || 'plan'}
                {settle.period ? ` (${cap(String(settle.period))})` : ''} · <strong>{money(settle.amount, settle)}</strong>
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Payment reference</label>
                <input
                  value={form.reference}
                  onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                  placeholder="UPI / bank transaction id"
                  data-testid="activate-reference"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Method</label>
                <select
                  value={form.method}
                  onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}
                  data-testid="activate-method"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] bg-white"
                >
                  {METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Note (optional)</label>
                <textarea
                  rows={2}
                  value={form.note}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  data-testid="activate-note"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] resize-none"
                />
              </div>
            </div>
        </ModalShell>
      )}

      {invoice && (
        <InvoiceModal payment={invoice} doctorId={doctorNameOf(invoice)} onClose={() => setInvoice(null)} />
      )}
    </div>
  );
}
